import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Coffee, Heart } from 'lucide-react';
import Button from '@/components/common/Button';

interface DonateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PAYPAL_LINK = 'https://paypal.me/matthiastieber';

function DonateModal({ isOpen, onClose }: DonateModalProps): React.ReactElement | null {
  const handleDonate = () => {
    window.open(PAYPAL_LINK, '_blank', 'noopener,noreferrer');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-background-darkest/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-background-card border border-border rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* Header with gradient */}
              <div className="relative bg-gradient-to-r from-primary/20 via-accent/20 to-secondary/20 p-6 text-center">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-lg hover:bg-background-elevated/50 transition-colors"
                >
                  <X className="h-5 w-5 text-foreground-muted" />
                </button>

                <motion.div
                  initial={{ rotate: -10 }}
                  animate={{ rotate: [0, -10, 10, -10, 0] }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-4"
                >
                  <Coffee className="h-8 w-8 text-primary" />
                </motion.div>

                <h2 className="text-xl font-bold text-foreground">
                  Spendier uns einen Kaffee!
                </h2>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <p className="text-foreground-muted text-center">
                  BOIS kostet uns ca. <span className="font-semibold text-foreground">5€/Monat</span> für
                  Hosting & Domain.
                </p>

                <div className="flex items-center justify-center gap-2 text-foreground-subtle">
                  <Heart className="h-4 w-4 text-destructive" />
                  <span className="text-sm">Jeder Euro hilft!</span>
                  <Heart className="h-4 w-4 text-destructive" />
                </div>

                <Button
                  onClick={handleDonate}
                  className="w-full"
                  size="lg"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Zu PayPal
                </Button>

                <p className="text-xs text-foreground-subtle text-center pt-2">
                  Danke, dass du die Bois unterstützt!
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default DonateModal;
